import React, { useState } from 'react';
import { useActionFileTypes, useGetDocuments } from '../../../../../../Hooks/TT_ELD/TruckInfo';
import { useParams } from 'react-router-dom';
import {
  Button,
  Col,
  Drawer,
  Form,
  Grid,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  TableProps,
} from 'antd';
import { truckInfo } from '../../../../../../API/TT_ELD/Users/truckInfo';
import DownloadButton from './DownloadButton';
import SearchOptions from '../../../../../../Utils/SearchOptions';
import {
  SearchResultForCompany,
  SearchResultForFindDriver,
} from '../../../../../../Utils/SearchResults';
import SearchInput from '../../../../../../Utils/SearchInput';
import useRouteQuery from '../../../../../../Utils/useRouterQuery';
import { sendEvent } from '../../../../../../Utils/analytics';
import { calcOrder } from '../../../../../components/core/calcOrder';
import dayjs from 'dayjs';
import { TableParams } from '../../../../../../Utils/TableTypes';
import { useTagQuerySync } from '../../../../../../Utils/useTagQuerySync';
import { EditOutlined, ExclamationCircleFilled, FilterOutlined } from '@ant-design/icons';

const { useBreakpoint } = Grid;

const { Option } = Select;

type Data = {
  data?: {
    data: Array<any>;
    count: number;
  };
  isLoading?: boolean;
  refetch?: any;
  isFetching?: boolean;
};

interface userDataSource {
  id: number | string;
  name: string;
  company: number | string;
  type: number | string;
  createdAt: number | string;
  edit: [];
  key: React.Key;
}

interface Params {
  id: string | undefined | any;

  [key: string]: string | undefined;
}

interface ActionFileTypeItem {
  id?: string | number;
  type: string;
  title: string;
  isCalendarExist?: boolean;
}

const Documents = () => {
  const { id } = useParams<Params>();
  const [companyName, setCompanyName] = useRouteQuery('', 'companyName', String);
  const [companyId, setCompanyId] = useState<number | string>('');
  const [userName, setUserName] = useRouteQuery('', 'userName', String);
  const [userId, setUserId] = useState<undefined | number | string>('');
  const [type, setType] = useRouteQuery('', 'type', (v) => String(v));
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [filterOpen, setFilterOpen] = useState(false);

  useTagQuerySync(
    { en_US: 'Documents', zh_CN: '' },
    'documents',
    { companyName, companyId, userName, userId, type, tableParams },
    {
      companyName: setCompanyName,
      companyId: setCompanyId,
      userId: setUserId,
      userName: setUserName,
      type: setType,
      tableParams: setTableParams,
    },
  );

  const [value, setValue] = useState<string>();

  const [logType, setLogType] = useState<string>();
  const [date, setDate] = useState<string>();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreatingFileType, setIsCreatingFileType] = useState(false);
  const [isUpdatingFileType, setIsUpdatingFileType] = useState(false);
  const [isDeletingFileType, setIsDeletingFileType] = useState(false);
  const [editingActionFileType, setEditingActionFileType] = useState<ActionFileTypeItem | null>(null);
  const [addForm] = Form.useForm<{
    type: string;
    title: string;
    isCalendarExist: boolean;
  }>();
  const [editForm] = Form.useForm<{
    type: string;
    title: string;
    isCalendarExist: boolean;
  }>();

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const canAddActionFileType = ['developer', 'manager'].includes(currentUser?.role);

  const dateOptions = Array.from({ length: 11 }, (_, i) => {
    const d = dayjs().subtract(i, 'day');
    return {
      label: d.format('DD-MM-YYYY'),
      value: d.format('DD-MM-YYYY'),
    };
  });

  const { data: actionFileTypes = [], refetch: refetchActionFileTypes } = useActionFileTypes(!!id) as {
    data: ActionFileTypeItem[];
    refetch: () => Promise<any>;
  };

  const selectedActionType = actionFileTypes.find((item) => item.type === logType);
  const showDateSelect = Boolean(selectedActionType?.isCalendarExist);

  const handleUpload = () => {
    if (logType) {
      sendCommandAndRefetch(logType, date);
    }
  };

  const handleCreateActionFileType = async () => {
    try {
      const values = await addForm.validateFields();
      setIsCreatingFileType(true);
      await truckInfo.createActionFileType({
        type: values.type.trim(),
        title: values.title.trim(),
        isCalendarExist: Boolean(values.isCalendarExist),
      });
      await refetchActionFileTypes();
      setIsAddModalOpen(false);
      addForm.resetFields();
    } finally {
      setIsCreatingFileType(false);
    }
  };

  const handleOpenEditActionFileType = (item: ActionFileTypeItem) => {
    setEditingActionFileType(item);
    editForm.setFieldsValue({
      type: item.type,
      title: item.title,
      isCalendarExist: Boolean(item.isCalendarExist),
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateActionFileType = async () => {
    if (!editingActionFileType) return;

    try {
      const values = await editForm.validateFields();
      setIsUpdatingFileType(true);
      await truckInfo.updateActionFileType(editingActionFileType.id || editingActionFileType.type, {
        type: values.type.trim(),
        title: values.title.trim(),
        isCalendarExist: Boolean(values.isCalendarExist),
      });

      await refetchActionFileTypes();

      if (logType === editingActionFileType.type) {
        setLogType(values.type.trim());
      }

      setIsEditModalOpen(false);
      setEditingActionFileType(null);
      editForm.resetFields();
    } finally {
      setIsUpdatingFileType(false);
    }
  };

  const handleDeleteActionFileType = async () => {
    if (!editingActionFileType) return;

    Modal.confirm({
      title: 'Delete action file type?',
      icon: <ExclamationCircleFilled />,
      content: 'Are you sure you want to delete this log type?',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setIsDeletingFileType(true);
          await truckInfo.deleteActionFileType(editingActionFileType.id || editingActionFileType.type);
          await refetchActionFileTypes();

          if (logType === editingActionFileType.type) {
            setLogType(undefined);
            setDate(undefined);
          }

          setIsEditModalOpen(false);
          setEditingActionFileType(null);
          editForm.resetFields();
        } finally {
          setIsDeletingFileType(false);
        }
      },
    });
  };

  const { data, refetch, isLoading, isFetching }: Data = useGetDocuments(
    id,
    userId,
    companyId,
    type,
    tableParams,
  );
  const columns: object[] = [
    {
      title: 'No',
      dataIndex: 'id',
      key: 'id',
      width: 65,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Download',
      dataIndex: 'edit',
      key: 'edit',
      width: 200,
      render: (links: any) => {
        return (
          <>
            {links.map((link: any) => (
              <DownloadButton key={link?.link} link={link?.link} fileName={link?.fileName} />
            ))}
          </>
        );
      },
    },
  ];

  const sendCommandAndRefetch = async (command: string, date?: string) => {
    await truckInfo.commandsPost({
      command: command,
      userId: id,
      extra: date ? { date } : undefined,
    });
    sendEvent('Documents Tab ', `Documents Button  ${command} `, `Button Clicked `, 1);

    refetch();
  };

  const handleChange = (event: string, inputName: string) => {
    const updateFunctions: Record<string, (value: string) => void> = {
      type: setType,
    };

    if (updateFunctions[inputName]) {
      updateFunctions[inputName](event?.trim());
      setTableParams({
        pagination: {
          current: 1,
        },
      });
    }
  };

  const handleTableChange: TableProps<any>['onChange'] = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  };

  const handleSearchSelect =
    (setter: (val: string) => void, nameSetter?: (val: string) => void) =>
    (val: any, { valId }: { valId: string }) => {
      setter(valId);
      nameSetter?.(val);
      if (valId) {
        setTableParams({
          pagination: {
            current: 1,
          },
        });
      }
    };

  return (
    <div className="mainPage">
      <Spin size="large" spinning={isLoading || isFetching}>
        {!id && (
          <>
            <Row
              gutter={[8, 8]}
              align="middle"
              justify="space-between"
              style={{ marginBottom: 15 }}
            >
              {!isMobile && (
                <>
                  <SearchOptions
                    SearchResult={SearchResultForCompany}
                    onSelect={handleSearchSelect(setCompanyId, setCompanyName)}
                    placeholder="Search by company"
                    value={companyName}
                  />
                  <SearchOptions
                    SearchResult={(query: string) => SearchResultForFindDriver(query)}
                    onSelect={handleSearchSelect(setUserId, setUserName)}
                    placeholder="User Search"
                    value={userName}
                  />

                  <SearchInput
                    value={type}
                    placeholder="Type Search"
                    onDebounceChange={(event: string) => handleChange(event, 'type')}
                  />

                  <Button style={{ marginLeft: 'auto' }} size={'large'} onClick={() => refetch()}>
                    Refresh
                  </Button>
                </>
              )}

              <Col
                xs={24}
                style={{
                  display: 'flex',
                  justifyContent: isMobile ? 'flex-end' : 'flex-start',
                }}
              >
                <Space>
                  {isMobile && (
                    <Button
                      onClick={() => setFilterOpen(true)}
                      size="large"
                      icon={<FilterOutlined />}
                    />
                  )}

                  {isMobile && (
                    <Button size="large" onClick={() => refetch()}>
                      Refresh
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>

            <Drawer
              title="Filters"
              placement="bottom"
              height="70%"
              onClose={() => setFilterOpen(false)}
              open={filterOpen}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <SearchOptions
                  SearchResult={SearchResultForCompany}
                  onSelect={handleSearchSelect(setCompanyId, setCompanyName)}
                  placeholder="Search by company"
                  value={companyName}
                />
                <SearchOptions
                  SearchResult={(query: string) => SearchResultForFindDriver(query)}
                  onSelect={handleSearchSelect(setUserId, setUserName)}
                  placeholder="User Search"
                  value={userName}
                />

                <SearchInput
                  value={type}
                  placeholder="Type Search"
                  onDebounceChange={(event: string) => handleChange(event, 'type')}
                />
              </Space>
            </Drawer>
          </>
        )}
        <Col style={{ margin: '0px 0 15px 0' }}>
          {id && (
            <Row
              gutter={[8, 8]}
              align="middle"
              justify="space-between"
              style={{ marginBottom: 15 }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '40%',
                }}
              >
                {/* <Button
                  style={{ marginRight: '10px' }}
                  type="primary"
                  onClick={() => sendCommandAndRefetch('upload_log_file')}
                >
                  Request Log
                </Button>
                <Button
                  style={{ marginRight: '10px' }}
                  type="primary"
                  onClick={() => sendCommandAndRefetch('upload_db_file')}
                >
                  Request DB
                </Button>
                <Button
                  style={{ marginRight: '10px' }}
                  type="primary"
                  onClick={() => sendCommandAndRefetch('upload_timer_file')}
                >
                  Upload Timer File
                </Button>
                <Button
                  style={{ marginRight: '10px' }}
                  type="primary"
                  onClick={() => sendCommandAndRefetch('delete_db_file')}
                >
                  Delete DB File
                </Button>
                <Button
                  style={{ marginRight: '10px' }}
                  type="primary"
                  onClick={() => sendCommandAndRefetch('upload_pref_file')}
                >
                  Upload Pref File
                </Button>
                <Button
                  style={{ marginRight: '10px' }}
                  type="primary"
                  onClick={() => sendCommandAndRefetch('upload_screenshot_file')}
                >
                  Upload Screenshot File
                </Button>
                <Button
                  style={{ marginRight: '10px' }}
                  type="primary"
                  onClick={() => sendCommandAndRefetch('upload_eld_log_file')}
                >
                  Upload ELD log file
                </Button>

                 */}

                <Select
                  placeholder="Select Log Type"
                  style={{ width: 220 }}
                  value={logType}
                  optionLabelProp="label"
                  onChange={(val) => {
                    setLogType(val);
                    setDate(undefined);
                  }}
                >
                  {actionFileTypes.map((item) => (
                    <Option key={item.type} value={item.type} label={item.title}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{item.title}</span>
                        {canAddActionFileType && (
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleOpenEditActionFileType(item);
                            }}
                          />
                        )}
                      </div>
                    </Option>
                  ))}
                </Select>

                {showDateSelect && (
                  <Select
                    placeholder="Select date"
                    style={{ width: 160 }}
                    value={date}
                    onChange={setDate}
                  >
                    {dateOptions.map((d) => (
                      <Option key={d.value} value={d.value}>
                        {d.label}
                      </Option>
                    ))}
                  </Select>
                )}

                <Button
                  type="primary"
                  onClick={handleUpload}
                  disabled={!logType || (showDateSelect && !date)}
                >
                  Confirm
                </Button>

                {canAddActionFileType && (
                  <Button
                    onClick={() => {
                      addForm.setFieldsValue({ isCalendarExist: false });
                      setIsAddModalOpen(true);
                    }}
                  >
                    Add
                  </Button>
                )}
              </div>

              <Button style={{ marginLeft: 'auto' }} size={'large'} onClick={() => refetch()}>
                Refresh
              </Button>
            </Row>
          )}
        </Col>

        <Table
          onChange={handleTableChange}
          dataSource={data?.data?.map((u: any, i: number): userDataSource => {
            const obj: userDataSource = {
              id: calcOrder(tableParams.pagination?.current ?? 1, 10, i),
              name: u?.user.first_name + ' ' + u?.user.second_name,
              company: u.company?.name + ' : ' + u?.company?.id,
              type: u.type,
              createdAt: u.createdAt,
              edit: u.links,
              key: u.id,
            };
            return obj;
          })}
          columns={columns}
          pagination={{
            total: data?.count,
            showSizeChanger: false,
            current: tableParams.pagination?.current,
          }}
          scroll={{ x: 'max-content' }}
          sticky
        />
      </Spin>

      <Modal
        title="Add Action File Type"
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        onOk={handleCreateActionFileType}
        okText="Add"
        confirmLoading={isCreatingFileType}
        destroyOnClose
      >
        <Form
          form={addForm}
          layout="vertical"
          initialValues={{ isCalendarExist: false }}
        >
          <Form.Item
            label="Type"
            name="type"
            rules={[
              { required: true, message: 'Type is required' },
              {
                pattern: /^[a-z0-9_]+$/,
                message: 'Use lowercase letters, numbers, underscores',
              },
            ]}
          >
            <Input placeholder="upload_new_file" />
          </Form.Item>

          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Title is required' }]}
          >
            <Input placeholder="Get New File" />
          </Form.Item>

          <Form.Item
            label="Calendar required"
            name="isCalendarExist"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Action File Type"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingActionFileType(null);
        }}
        onOk={handleUpdateActionFileType}
        okText="Save"
        confirmLoading={isUpdatingFileType}
        destroyOnClose
        footer={[
          <Button key="delete" danger loading={isDeletingFileType} onClick={handleDeleteActionFileType}>
            Delete
          </Button>,
          <Button
            key="cancel"
            onClick={() => {
              setIsEditModalOpen(false);
              setEditingActionFileType(null);
            }}
          >
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={isUpdatingFileType} onClick={handleUpdateActionFileType}>
            Save
          </Button>,
        ]}
      >
        <Form
          form={editForm}
          layout="vertical"
          initialValues={{ isCalendarExist: false }}
        >
          <Form.Item
            label="Type"
            name="type"
            rules={[
              { required: true, message: 'Type is required' },
              {
                pattern: /^[a-z0-9_]+$/,
                message: 'Use lowercase letters, numbers, underscores',
              },
            ]}
          >
            <Input placeholder="upload_new_file" />
          </Form.Item>

          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Title is required' }]}
          >
            <Input placeholder="Get New File" />
          </Form.Item>

          <Form.Item
            label="Calendar required"
            name="isCalendarExist"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Documents;
